import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Divider, Paper, Stack, Typography } from "@mui/material";
import { prepareInvoice } from "../../services/invoices";
import SelectedPresentation from "../SelectedPresentation";
import { buildOrderForCartView } from "../../pages/instances/sales/orders/orders-single/cartWorkspaceModel";

const formatAddress = (value) => {
  if (!value) return "—";
  if (typeof value === "string") return value;
  return [value.addressName, value.street, value.city, value.state || value.province, value.postalCode || value.zip, value.country]
    .filter(Boolean)
    .join(", ") || "—";
};

const moneyFormatter = (currency) => new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: String(currency || "CAD").toUpperCase(),
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const usePreparedInvoiceReview = ({ open, orderId, request, onPrepared, onInitialReviewSettled }) => {
  const [state, setState] = useState({ loading: false, error: "", review: null });
  const requestId = useRef(0);
  const requestKey = useMemo(() => JSON.stringify(request || {}), [request]);

  useEffect(() => {
    const preparedRequest = JSON.parse(requestKey);
    if (!open || !orderId || !preparedRequest?.salesOrderId) {
      setState({ loading: false, error: "", review: null });
      onPrepared?.(null);
      // A missing source is an actionable review state, not an infinite loader.
      if (open) onInitialReviewSettled?.(true);
      return undefined;
    }
    const id = ++requestId.current;
    // A changed invoice mode/amount/source invalidates the previous
    // preparation token immediately, before the new read-only review returns.
    // Both the Finance and Sales Order dialogs disable Confirm while pending.
    setState({ loading: true, error: "", review: null });
    onPrepared?.(null);
    onInitialReviewSettled?.(false);
    // Read-only review: do NOT create or save an Invoicing Cart here.
    prepareInvoice(orderId, preparedRequest)
      .then((review) => {
        if (id !== requestId.current) return;
        setState({ loading: false, error: "", review });
        onPrepared?.(review);
        onInitialReviewSettled?.(true);
      })
      .catch((error) => {
        if (id !== requestId.current) return;
        const message = error?.response?.data?.message || error?.message || "Unable to prepare invoice review.";
        setState({ loading: false, error: message, review: null });
        onPrepared?.(null);
        onInitialReviewSettled?.(true);
      });
    return () => { requestId.current += 1; };
  }, [open, onPrepared, onInitialReviewSettled, orderId, requestKey]);

  return state;
};

const Detail = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{children || "—"}</Typography>
  </Box>
);

export default function InvoiceCreationReview({ open, orderId, order = {}, fetchOrder, request, currency = "CAD", onPrepared, onInitialReviewSettled }) {
  const { loading, error, review } = usePreparedInvoiceReview({ open, orderId, request, onPrepared, onInitialReviewSettled });
  const viewOrder = useMemo(() => buildOrderForCartView(order || {}, review?.workspace || {}), [order, review?.workspace]);
  const viewWorkspace = useMemo(() => ({
    source: "sales-order-cart",
    editable: false,
    dirty: false,
    cartPayload: review?.workspace || {},
    capabilities: { editItem: false, copyItem: false, deleteItem: false, addProduct: false, addOtherService: false, sections: false, reorder: false, adjustments: false },
  }), [review?.workspace]);
  const reviewCurrency = review?.invoice?.currency || currency;
  const formatMoney = useMemo(() => moneyFormatter(reviewCurrency), [reviewCurrency]);

  if (loading) return <Stack alignItems="center" spacing={1} sx={{ py: 4 }}><CircularProgress size={28} /><Typography variant="body2">Preparing authoritative invoice…</Typography></Stack>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!review) return <Alert severity="info">Choose invoice options to prepare the review.</Alert>;

  return (
    <Paper variant="outlined" sx={{ mt: 2, overflow: "hidden" }} data-testid="invoice-creation-review">
      <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
        <Typography variant="subtitle1" fontWeight={700}>Invoice review</Typography>
        <Typography variant="caption" color="text.secondary">Review exactly what will be invoiced before creating the record.</Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2, maxHeight: "48vh", overflowY: "auto" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2 }}>
          <Detail label="Customer">{review.customer?.company}</Detail>
          <Detail label="Contact">{review.customer?.contact}</Detail>
          <Detail label="Project #">{review.project?.projectNumber}</Detail>
          <Detail label="Bill To">{formatAddress(review.customer?.billTo)}</Detail>
          <Detail label="Ship To">{formatAddress(review.customer?.shipTo)}</Detail>
          <Detail label="Customer PO">{review.project?.customerPo}</Detail>
          <Detail label="Source Sales Order">{review.source?.estimationId}</Detail>
          <Detail label="Revision">{review.source?.revision}</Detail>
          <Detail label="Invoice Date">{review.invoice?.date ? new Date(review.invoice.date).toLocaleDateString("en-CA") : "—"}</Detail>
          <Detail label="Terms">{review.invoice?.terms}</Detail>
          <Detail label="Currency">{reviewCurrency}</Detail>
          <Detail label="Invoice role / mode">{`${review.invoice?.role || "—"} / ${review.invoice?.mode || "—"}`}</Detail>
        </Box>
        <Typography variant="overline" sx={{ display: "block", mt: 2 }}>Invoice preview</Typography>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          This is a read-only preview of the approved Sales Order. An editable Invoicing Cart will be created and linked when you create the invoice.
        </Alert>
        <SelectedPresentation
          order={viewOrder}
          fetchOrder={fetchOrder}
          handleShowProductsPopup={() => {}}
          cartWorkspace={viewWorkspace}
        />
        <Divider sx={{ my: 2 }} />
        <Stack alignItems="flex-end" spacing={0.5} sx={{ mt: 2 }}>
          {[["Subtotal", review.totals?.subtotal], ["Tax", review.totals?.tax], ["Total", review.totals?.total], ["Deposit requested", review.payments?.depositRequested], ["Deposit received", review.payments?.depositReceived], ["Other payments", review.payments?.otherPayments], ["Remaining before", review.payments?.remainingBefore], ["Proposed invoice amount", review.payments?.proposedInvoiceAmount], ["Remaining after", review.payments?.remainingAfter]].map(([label, value]) => <Typography key={label} variant="body2"><Box component="span" fontWeight={700}>{label}: </Box>{formatMoney.format(value || 0)}</Typography>)}
        </Stack>
      </Box>
    </Paper>
  );
}
